// src/features/kommunikation/hooks/useDocuments.ts

import { useState, useEffect, useCallback } from 'react';
import { DocumentWithCredentials, DocumentFilter, DocumentStats } from '../types';
import { communicationDocumentService } from '../services/CommunicationDocumentService';
import { versandnachweisService } from '../services/VersandnachweisService';
import { supabase } from '../../../shared/services/supabase';

/**
 * Gibt den aktuellen Monat und das Jahr zurück
 */
const getCurrentMonthYear = (): { monat: number; jahr: number } => {
  const now = new Date();
  return {
    monat: now.getMonth() + 1,
    jahr: now.getFullYear()
  };
};

/**
 * Konvertiert eine File in einen reinen Base64-String (ohne data:-Prefix).
 */
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('FileReader result is not a string'));
        return;
      }
      // dataURL hat die Form "data:application/pdf;base64,JVBERi0xLjQ..."
      // wir wollen nur den Teil nach dem Komma.
      const commaIdx = result.indexOf(',');
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(new Error('FileReader failed'));
    reader.readAsDataURL(file);
  });
};

export const useDocuments = () => {
  const [documents, setDocuments] = useState<DocumentWithCredentials[]>([]);
  const [stats, setStats] = useState<DocumentStats>({
    total: 0,
    geplant: 0,
    versendet: 0,
    fehler: 0,
    ohne_credentials: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Default-Filter: Aktueller Monat
  const { monat, jahr } = getCurrentMonthYear();
  const [filter, setFilter] = useState<DocumentFilter>({ monat, jahr });

  /**
   * Lädt Dokumente mit Filter und berechnet Stats aus den geladenen Daten
   */
  const loadDocuments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await communicationDocumentService.getDocumentsWithCredentials(filter);
      setDocuments(data);
      
      // Stats direkt aus den geladenen Daten berechnen (kein separater Query)
      setStats({
        total: data.length,
        geplant: data.filter(d => d.versand_status === 'geplant').length,
        versendet: data.filter(d => d.versand_status === 'versendet').length,
        fehler: data.filter(d => d.versand_status === 'fehler').length,
        ohne_credentials: data.filter(d => !d.hasCredential).length
      });
      
    } catch (err) {
      console.error('Error loading documents:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Laden der Dokumente');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  /**
   * Lädt Daten initial und bei Filter-Änderung
   */
  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  /**
   * Aktualisiert einen Filter
   */
  const updateFilter = useCallback((newFilter: Partial<DocumentFilter>) => {
    setFilter(prev => ({ ...prev, ...newFilter }));
  }, []);

  /**
   * Setzt Filter zurück (auf aktuellen Monat)
   */
  const resetFilter = useCallback(() => {
    const { monat: m, jahr: j } = getCurrentMonthYear();
    setFilter({ monat: m, jahr: j });
  }, []);

  /**
   * Löscht ein Dokument
   */
  const deleteDocument = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      const result = await communicationDocumentService.deleteDocument(documentId);
      
      if (result.success) {
        await loadDocuments();
        return true;
      } else {
        setError(result.error || 'Löschen fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error deleting document:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Löschen');
      return false;
    }
  }, [loadDocuments]);

  /**
   * Aktualisiert Versand-Status
   * Ruft korrekt updateVersandStatus auf (nicht updateStatus!)
   */
  const updateStatus = useCallback(async (
    documentId: string,
    status: 'geplant' | 'versendet' | 'fehler' | 'abgebrochen',
    fehlerNachricht?: string
  ): Promise<boolean> => {
    try {
      const result = await communicationDocumentService.updateVersandStatus(
        documentId,
        status,
        fehlerNachricht
      );
      
      if (result.success) {
        await loadDocuments();
        return true;
      } else {
        setError(result.error || 'Status-Update fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Status-Update');
      return false;
    }
  }, [loadDocuments]);

  /**
   * Sendet ein Dokument sofort (erstmalig oder erneut)
   */
  const sendDocumentNow = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      setError(null);
      
      const result = await communicationDocumentService.sendDocumentNow(documentId);
      
      if (result.success) {
        // Dokumente neu laden um aktualisierten Status zu zeigen
        await loadDocuments();
        return true;
      } else {
        setError(result.error || 'Versand fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error sending document:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Versand');
      return false;
    }
  }, [loadDocuments]);

  /**
   * Sendet ein Dokument als Test an lohn@xtax.de
   * Status bleibt unverändert, Betreff wird mit [TEST] markiert
   */
  const sendTestDocument = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      setError(null);
      
      const result = await communicationDocumentService.sendTestDocumentNow(documentId);
      
      if (result.success) {
        // Kein Reload nötig, da Status unverändert bleibt
        return true;
      } else {
        setError(result.error || 'Testversand fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error sending test document:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Testversand');
      return false;
    }
  }, []);

  /**
   * NEU: Tauscht das PDF eines Dokuments im Storage aus.
   * Empfaenger-Link, Passwort und Versandstatus bleiben unveraendert.
   * Es wird KEINE E-Mail versendet.
   */
  const replaceDocument = useCallback(async (
    documentId: string,
    file: File
  ): Promise<boolean> => {
    try {
      setError(null);

      // Datei zu Base64 konvertieren
      const fileBase64 = await fileToBase64(file);

      // Aktuell eingeloggter Supabase-User fuer Audit-Trail
      const { data: userData } = await supabase.auth.getUser();
      const requestingUserEmail = userData.user?.email || '';

      if (!requestingUserEmail) {
        setError('Kein eingeloggter User gefunden. Bitte erneut anmelden.');
        return false;
      }

      const result = await communicationDocumentService.replaceDocument(
        documentId,
        fileBase64,
        file.name,
        requestingUserEmail
      );

      if (result.success) {
        await loadDocuments();
        return true;
      } else {
        setError(result.error || 'Austausch fehlgeschlagen');
        return false;
      }
    } catch (err) {
      console.error('Error replacing document:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Austausch');
      return false;
    }
  }, [loadDocuments]);

  /**
   * NEU: Erzeugt den Versand- und Abrufnachweis als PDF und startet den Download.
   * Rein lesend: es werden keine Daten veraendert, kein Reload noetig.
   */
  const generateProof = useCallback(async (documentId: string): Promise<boolean> => {
    try {
      setError(null);

      const result = await versandnachweisService.generateVersandnachweis(documentId);

      if (result.success) {
        return true;
      } else {
        setError(result.error || 'Versandnachweis konnte nicht erzeugt werden');
        return false;
      }
    } catch (err) {
      console.error('Error generating proof:', err);
      setError(err instanceof Error ? err.message : 'Fehler bei der Nachweis-Erzeugung');
      return false;
    }
  }, []);

  return {
    documents,
    stats,
    loading,
    error,
    filter,
    updateFilter,
    resetFilter,
    loadDocuments,
    deleteDocument,
    updateStatus,
    sendDocumentNow,
    sendTestDocument,
    replaceDocument,
    generateProof
  };
};
